import type { Image_Type } from '../../__generated__/graphql-client-types';
import { NO_IMAGE_URL } from '../../constants/home/general';
import {
	type InternalAuthor,
	type Fields,
	type SeoMetaData,
	type SeoMetaDataContent,
	type ExternalAuthor,
	type HeroMedia,
	type ImageContent
} from '../../types/construct.types';

const isInternalAuthor = (fields: Fields): fields is InternalAuthor => 'profile_id' in fields;
const isExternalAuthor = (fields: Fields): fields is ExternalAuthor => 'guest_author' in fields;
const isHeroMediaImage = (fields: Fields): fields is HeroMedia => 'media_image' in fields;

export function getSeoMetaData(seo: SeoMetaDataContent): SeoMetaData {
	return {
		title: seo.fields.meta_title ?? '',
		description: seo.fields.meta_description ?? '',
		isIndexed: seo.fields.indexed === 'true',
		socialMedia: {
			image: seo.fields.social_media_image?.image ?? undefined,
			title: seo.fields.social_media_title ?? '',
			description: seo.fields.social_media_description ?? ''
		}
	};
}

export const getArticleAuthorName = (fields: Fields): string | null => {
	if (isInternalAuthor(fields)) {
		const [selectedProfile] = fields.profile_id.items;
		return selectedProfile?.detail?.profile?.name ?? null;
	}

	if (isExternalAuthor(fields)) {
		return fields.guest_author.fields.full_name;
	}

	return null;
};

export const getArticleHeroImageId = (fields: Fields): string | null => {
	if (isHeroMediaImage(fields)) {
		return fields.media_image?.image?.id ?? null;
	}

	return null;
};

/* Represents a construct image with default values for missing fields */
export type NormalizedConstructImage = {
	id: string;
	imageType: Image_Type;
	description: string;
	lowQualityPlaceholder: string;
};

export const normalizeConstructImage = (image?: ImageContent): NormalizedConstructImage => ({
	id: image?.image?.id ?? NO_IMAGE_URL,
	imageType: image?.image?.imageType ?? 'private',
	description: image?.image?.description ?? '',
	lowQualityPlaceholder: image?.image?.lowQualityPlaceholder ?? ''
});
